import Asset from '../asset';
import Cta, { CtaStyle, CtaType } from '../../cta';
import Platform from '../../platform';
import { StyleTag } from '../../util/create-style-tag';
import { PageNotReady } from '../../errors';
import { AssetUpdated, OlError, StartPrequal } from '../../events';
import { AssetHTMLElement } from '../../typings';
import configuration from '../../configuration';
import { logToPartnerVisit } from '../../util/logging';
import getCtaType from '../../util/cta-type';

export default abstract class AssetWrapper<AssetClass extends Asset = Asset> {
  protected _cta?: Cta;
  protected _asset?: AssetClass;
  constructor(
    // Defining attributes in a constructor with a protection modifier
    // also defines them as class attributes. Ex, container is a read-only
    // attribute of AssetWrapper (AssetWrapper.container)
    readonly container: AssetHTMLElement,
    readonly platform: Platform
  ) {
    if (this.container.olAssetWrapper) throw new Error('HTMLElement already bounded to an AssetWrapper.');
    else this.container.olAssetWrapper = this;
    this.platform.addStyleToDocument(this.getDocumentStyles());
  }

  /**
   * Create CTA if it doesn't exist, then return the CTA
   */
  get cta(): Cta {
    if (!this._cta) {
      this._cta = this.createCta();
    }
    return this._cta;
  }

  get asset(): AssetClass {
    if (!this._asset) {
      this._asset = this.parseAssetData();
    }
    return this._asset;
  }

  /**
   * Render a CTA with the asset data provided by this AssetWrapper
   */
  render(e?: Event, forceCtaRender?: boolean) {
    try {
      // try to reparse asset data, but don't re-render, we're already doing that
      this.reparseAssetData(false);
      if (this.shouldRender()) {
        this.cta.render(e, forceCtaRender);
        if (this.cta.isRendered()) {
          logToPartnerVisit(this.platform.pageType).catch((reason) => console.log(reason));
        }
      } else {
        this.cta.remove();
      }
    } catch (error) {
      if (error instanceof PageNotReady) {
        // If the page isn't ready yet, attempt the render on the next cycle
        console.debug('Page not ready, waiting...');
        this.platform.onNextCycle(this.render.bind(this));
      } else {
        // Otherwise, throw a fatal error on eventBus
        this.platform.eventBus.dispatchEvent(new OlError({ detail: { error, msg: 'Failed to render CTA' } }));
      }
    }
  }

  get parseIndex(): number | null {
    const val = this.container.olParserIndex;
    return val !== undefined ? val : null;
  }

  /**
   * Updates `AssetWrapper._asset` and calls `AssetWrapper.render` _only if the asset has changed_.
   * If the asset has not changed, no actions occur.
   */
  reparseAssetData(rerender = true): void {
    const asset = this.parseAssetData();
    if (asset.equals(this.asset)) {
      // no updates have occurred, do nothing.
      return;
    }

    console.debug('asset updated');
    const evt = new AssetUpdated({ detail: { asset, wrapper: this } });
    this._asset = asset;
    if (rerender) this.render(evt); // we call this func from this.render(), so we don't want to call render again
  }

  /**
   * Parse the Asset data from the Container
   */
  abstract parseAssetData(): AssetClass;

  /**
   * Create the CTA HTMLElement based on the asset
   */
  createCta(): Cta {
    console.warn('createCta() was called but is not implemented for this wrapper.');
    return {} as Cta;
  }

  /**
   * Create the CTA HTMLElement based on the class
   */
  createCtaBasedOnPartnerClass(className: string): HTMLElement {
    const ctaType: CtaType = getCtaType(
      this.platform.pageType,
      this.cta.style,
      this.platform.configuration.partner?.name
    );

    configuration.cta_type = ctaType;

    const target = this.getRenderLocation();

    const wrapperDiv = document.createElement('div');
    wrapperDiv.classList.add(className);

    const cta = document.createElement('a');
    cta.textContent = configuration.cta_text;

    cta.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopPropagation();

        this.platform.eventBus.dispatchEvent(
          new StartPrequal({
            detail: {
              asset: this.asset,
              cta_type: ctaType
            }
          })
        );
      },
      true
    );

    wrapperDiv.appendChild(cta);
    target.appendChild(wrapperDiv);

    return wrapperDiv;
  }

  /**
   * Find the render location for the CTA within the container, and return the HTML element
   * the CTA will be _APPENDED_ to. Throwing a PageNotReady error within this function
   * will cause `cta.render` to be automatically called on the next cycle.
   */
  abstract getRenderLocation(style?: CtaStyle): HTMLElement;

  /**
   * Returns the element where the CTA should be rendered. This function will:
   * 1. Check if the Target Location already exists within the container.
   * 2. Check if the renderLocation specified by `selector` exists
   * 3. Create the Target location and prepend it to the renderLocation
   * @param selector: String selector used to determine the renderLocation
   * @param tag: String tag of element which will be the renderLocation
   */
  prependRenderLocationBySelector(selector: string, tag = 'div'): HTMLElement {
    let targetLocation = this.container.querySelector<HTMLElement>('.ol-target-element');
    if (targetLocation) return targetLocation;

    const renderLocation = this.container.querySelector(selector);
    if (!renderLocation) throw new PageNotReady('Render location not found in the container.');

    targetLocation = document.createElement(tag);
    targetLocation.classList.add('ol-target-element');
    renderLocation.prepend(targetLocation);

    return targetLocation;
  }

  /**
   * Determine if the CTA should be rendered based on the Asset Data
   */
  shouldRender(): boolean {
    return !!(this.asset && this.container) && !this.asset.filterCTA(configuration.filter_groups);
  }

  getDocumentStyles(): StyleTag[] {
    return [];
  }

  /**
   * Returns the parent container on the page that hold all the assets.
   * When overwriting this static method, we should try to minimize the size of the child subtree
   * of this element because we gonna use this element to attach the page update MutationObserver.
   * @returns {HTMLElement} the parent(immediate or not) container of the assets containers
   */
  static getPageContainer(): HTMLElement {
    return document.body;
  }

  /**
   * Decide if the Platform object should add or not a MutationObserver to trigger page-update events
   * when there is changes on the DOM subtree of the page container.
   * @returns {boolean}
   */
  static shouldWatchElements(): boolean {
    return false;
  }

  /**
   * Return the options used to initialize the MutationObserver that will
   * watch for page updates.
   * @returns {MutationObserverInit}
   */
  static getPageUpdateMutationObserverOptions(): MutationObserverInit {
    return { childList: true, subtree: false };
  }
}

export type AssetWrapperConstructor = {
  getPageUpdateMutationObserverOptions(): MutationObserverInit;
  shouldWatchElements(): boolean;
  getPageContainer(): HTMLElement;
  new (container: HTMLElement, platform: Platform): AssetWrapper;
};
